export const MEDIA_CHUNK_BYTES = 64 * 1024;
export type MediaMime = "image/png" | "image/jpeg" | "image/webp";
export type StoredMediaImage = { id: string; mime: MediaMime; data: string; createdAt: string; createdBy: string };
export type MediaUploadResult = { url: string; imageId?: string };

/** Small authenticated requests fit the existing proxy limit without recompressing images. */
export async function uploadAdminImage(file: File, kind: "payment" | "promotion", signal?: AbortSignal): Promise<MediaUploadResult> {
  const limit = kind === "payment" ? 512 * 1024 : 2 * 1024 * 1024;
  if (!file.size || file.size > limit || !["image/png", "image/jpeg", "image/webp"].includes(file.type)) throw new Error(kind === "payment" ? "请选择 512 KiB 以内的 PNG、JPEG 或 WebP 图片。" : "请选择 2 MiB 以内的 PNG、JPEG 或 WebP 图片。");
  const combined = signal ? AbortSignal.any([signal, AbortSignal.timeout(120000)]) : AbortSignal.timeout(120000);
  async function request<T>(url: string, method: string, body?: unknown): Promise<T> {
    const response = await fetch(url, { method, credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body), signal: combined });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || "图片上传失败，请重试。");
    return payload;
  }
  const { uploadId } = await request<{ uploadId: string }>("/api/admin/media/uploads", "POST", { kind, mime: file.type, size: file.size });
  try {
    for (let offset = 0; offset < file.size; offset += MEDIA_CHUNK_BYTES) {
      const bytes = new Uint8Array(await file.slice(offset, offset + MEDIA_CHUNK_BYTES).arrayBuffer());
      let binary = "";
      for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
      await request(`/api/admin/media/uploads/${uploadId}`, "PUT", { offset, chunk: btoa(binary) });
    }
    return await request<MediaUploadResult>(`/api/admin/media/uploads/${uploadId}/complete`, "POST", {});
  } catch (error) {
    // Discard only the temporary assembly, never a saved image or historical order.
    void fetch(`/api/admin/media/uploads/${uploadId}`, { method: "DELETE", credentials: "same-origin" }).catch(() => {});
    throw error;
  }
}
